import React from 'react';
import { ShieldCheck, TrendingUp, Users, Globe, Library, Handshake, Gem, BarChart, Cpu, GraduationCap, Rocket, Tv } from 'lucide-react';
import StrategyMapNode from '../components/strategy/StrategyMapNode';
import ConnectingLine from '../components/strategy/ConnectingLine';
import PerspectiveHeader from '../components/strategy/PerspectiveHeader';

function StrategyContentFY2526() {
  return (
    <>
      <div className="relative text-center space-y-4">
        <div className="inline-block bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-gray-200/50 max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600 mb-2">Vision</h3>
          <p className="text-gray-700 text-lg">Leading creator of new age IPs made in India - captivating audiences worldwide.</p>
        </div>
        <ConnectingLine short />
        <div className="inline-block bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-gray-200/50 max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600 mb-2">Mission</h3>
          <p className="text-gray-700 text-lg">To pioneer the future of mobile-first entertainment by empowering creators to innovate, captivating global audiences, and building a profitable, globally recognized brand from India.</p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="relative space-y-6">
          <ConnectingLine />
          <PerspectiveHeader title="Financial Perspective" textColor="text-green-700" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6">
            <StrategyMapNode title="Meet AOP Goals" icon={ShieldCheck} iconColor="bg-green-500" items={["Execute on the Annual Operating Plan to ensure profitability and sustainable operations."]} kpis={["Revenue: 195.6 Cr", "Gross Margin: 25%", "EBITDA: 18.6 Cr"]} />
            <StrategyMapNode title="Build Growth Assets" icon={TrendingUp} iconColor="bg-green-500" items={["Invest in creating and acquiring IPs with long-term, global potential to build enterprise value."]} kpis={["3+ IPs with >$5M global potential", "5+ global format adoptions", "22 Cr+ from global markets"]} />
          </div>
        </div>

        <div className="relative space-y-6">
          <ConnectingLine />
          <PerspectiveHeader title="Customer Perspective" textColor="text-blue-700" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6">
            <StrategyMapNode title="Deepen Domestic Dominance" icon={Users} iconColor="bg-blue-500" items={["Be the indispensable partner for brands and platforms wanting to reach young India."]} kpis={["Highest watch-time in category", "Top partner status with key streamers"]} />
            <StrategyMapNode title="Drive Global Expansion" icon={Globe} iconColor="bg-blue-500" items={["Adapt our successful content creation model for new international markets."]} kpis={["# of new geographies entered", "# of localized formats launched"]} />
          </div>
        </div>

        <div className="relative space-y-6">
          <ConnectingLine />
          <PerspectiveHeader title="Internal Processes" textColor="text-yellow-700" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 pt-6">
            <StrategyMapNode title="IP Engine" icon={Library} iconColor="bg-yellow-500" items={["Refine the 'art-to-science' process of creating successful IPs."]} kpis={["Increase IP hit-ratio to 75%"]} />
            <StrategyMapNode title="B2B Sales & Monetization" icon={Handshake} iconColor="bg-yellow-500" items={["Maximize the value of each IP with a multi-pronged revenue strategy."]} kpis={["Achieve >2 revenue streams per IP"]} />
            <StrategyMapNode title="B2B Customer Excellence" icon={Gem} iconColor="bg-yellow-500" items={["Deliver exceptional results and service to our brand and platform partners."]} kpis={["Exceed partner KPIs", "Win 5+ industry awards"]} />
            <StrategyMapNode title="Viewership & Marketing" icon={BarChart} iconColor="bg-yellow-500" items={["Use technology and network effects to grow our audience efficiently."]} kpis={["Increase views per viewer by 25%"]} />
            <StrategyMapNode title="Tech & AI Adoption" icon={Cpu} iconColor="bg-yellow-500" items={["Leverage technology to create scalable systems and a competitive edge."]} kpis={["Reduce turnaround time by 15%"]} />
          </div>
        </div>

        <div className="relative space-y-6">
          <ConnectingLine />
          <PerspectiveHeader title="Organizational Capacity" textColor="text-purple-700" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-6">
            <StrategyMapNode title="Teamwork & Culture" icon={Users} iconColor="bg-purple-500" items={["Build a collaborative, high-performance environment based on our core values."]} />
            <StrategyMapNode title="Trainings & Learning" icon={GraduationCap} iconColor="bg-purple-500" items={["Equip our team with the skills needed for the future of entertainment."]} />
            <StrategyMapNode title="Building Global B2B Teams" icon={Rocket} iconColor="bg-purple-500" items={["Develop a world-class sales organization to drive our global ambitions."]} />
          </div>
        </div>
      </div>
    </>
  );
}

function StrategyContentFY2627() {
  const priorities = [
    "Scale Alright TV aggressively while maintaining disciplined ROAS",
    "Expand globally through US licensing and co-productions",
    "Build India's largest Gen-Z first content slate",
    "Establish leadership in AI-powered entertainment workflows",
    "Improve operational efficiency and cost optimization",
    "Build long-term enterprise value through scalable IP ownership",
  ];

  return (
    <>
      <div className="relative text-center space-y-4">
        <div className="inline-block bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-gray-200/50 max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600 mb-2">Vision</h3>
          <p className="text-gray-700 text-lg">Entertainment from India for the world — building globally scalable, mobile-first IPs across formats, platforms, and geographies.</p>
        </div>
        <ConnectingLine short />
        <div className="inline-block bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-gray-200/50 max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600 mb-2">Mission</h3>
          <p className="text-gray-700 text-lg">To pioneer the future of mobile-first entertainment by empowering creators, captivating global audiences, and building a profitable, globally recognized entertainment company from India.</p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="relative space-y-6">
          <ConnectingLine />
          <PerspectiveHeader title="Financial Perspective" textColor="text-green-700" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6">
            <StrategyMapNode title="Revenue & Profitability" icon={ShieldCheck} iconColor="bg-green-500" items={["Deliver on FY27 revenue, margin, and EBITDA targets."]} kpis={["Revenue: ₹630.3 Cr", "Gross Margin: ₹200.1 Cr (32%)", "EBITDA: Breakeven"]} />
            <StrategyMapNode title="Subscription & Global Growth" icon={TrendingUp} iconColor="bg-green-500" items={["Diversify revenue through platform subscriptions and US-led international expansion."]} kpis={["Platform Subscription Revenue: ₹104 Cr", "5+ global format adaptations", "₹50 Cr+ US streaming revenue"]} />
          </div>
        </div>

        <div className="relative space-y-6">
          <ConnectingLine />
          <PerspectiveHeader title="Customer Perspective" textColor="text-blue-700" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-6">
            <StrategyMapNode title="Deepen Domestic Dominance" icon={Users} iconColor="bg-blue-500" items={["Stay the indispensable Gen-Z entertainment partner in India."]} kpis={["1Bn+ monthly ecosystem views"]} />
            <StrategyMapNode title="Scale Alright TV" icon={Tv} iconColor="bg-blue-500" items={["Grow Alright TV into a leading premium mobile entertainment platform."]} kpis={["100K+ DAUs", "Retention: 40%"]} />
            <StrategyMapNode title="Drive Global Expansion" icon={Globe} iconColor="bg-blue-500" items={["Expand presence across US and Rest-of-World markets."]} />
          </div>
        </div>

        <div className="relative space-y-6">
          <ConnectingLine />
          <PerspectiveHeader title="Internal Processes" textColor="text-yellow-700" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 pt-6">
            <StrategyMapNode title="IP Engine" icon={Library} iconColor="bg-yellow-500" items={["Strengthen the IP engine across scripted, unscripted, anime, and vertical dramas."]} />
            <StrategyMapNode title="Production & Ad Sales" icon={Handshake} iconColor="bg-yellow-500" items={["Produce 5₹134 Cr India ad sales target"]} />
            <StrategyMapNode title="Audience Growth Flywheel" icon={BarChart} iconColor="bg-yellow-500" items={["Build a scalable audience growth flywheel across the ecosystem."]} />
            <StrategyMapNode title="AI in Workflows" icon={Cpu} iconColor="bg-yellow-500" items={["Leverage AI for anime, post-production, and operational workflows."]} />
            <StrategyMapNode title="Cost Optimization" icon={Gem} iconColor="bg-yellow-500" items={["Optimize costs through studio infrastructure and operational leverage."]} />
          </div>
        </div>

        <div className="relative space-y-6">
          <ConnectingLine />
          <PerspectiveHeader title="Organizational Capacity" textColor="text-purple-700" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pt-6">
            <StrategyMapNode title="Teamwork & Culture" icon={Users} iconColor="bg-purple-500" items={["Build a collaborative, high-performance culture."]} />
            <StrategyMapNode title="Learning & Upskilling" icon={GraduationCap} iconColor="bg-purple-500" items={["Invest in AI, creative, and leadership upskilling."]} />
            <StrategyMapNode title="Global B2B & Licensing" icon={Rocket} iconColor="bg-purple-500" items={["Build global B2B and licensing teams."]} />
            <StrategyMapNode title="International Partnerships" icon={Handshake} iconColor="bg-purple-500" items={["Expand international partnerships and market expertise."]} />
          </div>
        </div>

        <div className="relative space-y-6">
          <ConnectingLine />
          <PerspectiveHeader title="FY27 Strategic Priorities" textColor="text-indigo-700" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
            {priorities.map((text, i) => (
              <div key={i} className="bg-white/70 backdrop-blur-sm p-5 rounded-2xl shadow-lg border border-gray-200/50 flex items-start transition-all duration-300 hover:shadow-2xl hover:scale-105">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold flex items-center justify-center mr-3">
                  {i + 1}
                </div>
                <p className="text-sm font-medium text-gray-800 leading-snug">{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative space-y-6">
          <ConnectingLine />
          <div className="text-center">
            <div className="inline-block bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-gray-200/50 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600 mb-2">Long-Term Aspiration</h3>
              <p className="text-gray-700 text-lg">Build India's most valuable, globally recognized mobile-first entertainment and IP company.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function StrategyView({ activeFY = 'FY2526' }) {
  const isFY2627 = activeFY === 'FY2627';
  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-gray-50 to-indigo-100 rounded-2xl shadow-inner">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight">
          Rusk Media Strategy Map <span className="text-indigo-600">— {isFY2627 ? 'FY 26-27' : 'FY 25-26'}</span>
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">From Vision to Action: A visual guide to our company's journey to success.</p>
      </div>

      <div className="space-y-6 relative">
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-200 via-indigo-300 to-blue-300 transform -translate-x-1/2 hidden md:block" />
        {isFY2627 ? <StrategyContentFY2627 /> : <StrategyContentFY2526 />}
      </div>
    </div>
  );
}
